import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  combineLatest,
  concatMap,
  EMPTY,
  filter,
  first,
  from,
  iif,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementFrom,
  CanvasElementId,
  CanvasElementType,
} from '@blaze-runtime/engine-domain';
import { TimedObservableValueCache } from '@blaze-runtime/engine-util';
import {
  ComparisonOperator,
  ConnectorTarget,
  FontWeightOption,
  LogicalQueryNode,
  PageType,
} from '@blaze-shared/api-contracts-domain';
import {
  ConnectorsCollectionService,
  ConnectorsTemplatesCollectionService,
} from '@blaze-shared/connectors-data-access';
import { AlertsApiService } from '@blaze-shared/datasets-data-access';
import { getEmptyLogicalNode, SingleColumnComparison } from '@blaze-shared/datasets-domain';
import {
  CanvasElementDataProperties,
  DataBindingsMode,
  DataBindingSourceInfo,
  FormListModelField,
  PropertiesConfigInputListFiltersModel,
  PropertiesConfigInputOptionsSelect,
  PropertiesConfigInputOptionsSelectApiSelectors,
  PropertiesConfigInputOptionsSelectDataValue,
  SchemaColumnSelectOption,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService, TimeZonesApiService } from '@blaze-shared/platform-data-access';
import { sortByKey } from '@blaze-shared/platform-util';
import { PagesApiService } from '@blaze-shared/projects-data-access';
import { getColumnNameLatestOrUnavailable, SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  COLUMN_ID_OR_NAME,
  ColumnFieldOption,
  DatasetColumn,
  findColumn,
  getModelField,
  hasBlazeTableFlag,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { SelectOption } from '@blaze-shared/shared-util';
import { type Nullish } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { FontsService } from '@blaze-shared/theming-data-access';
import { StyleSheetTarget } from '@blaze-shared/theming-domain';
import {
  findFirstNodeInWorkflowOfType,
  FormActionType,
  getValueByPath,
  isGetNode,
} from '@blaze-shared/workflow-domain';

import {
  refreshBinding,
  updateBinding,
  updateElementProps,
} from '../+state/elements/elements.actions';
import { getElementById, getElementEntities } from '../+state/elements/elements.selectors';
import { hasDataProps, hasProps } from '../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../canvas-elements/element-model/element-type-guards';
import { FormContainerPropsModel } from '../elements/form-container/form-container.model';
import { FormFieldControlsService } from '../services/forms/editor/public-api';
import { SearchAndFiltersWrapperService } from '../services/search-and-filters-wrapper.service';
import { ElementStylesService } from '../services/styles';

export type SelectKnobOption = SelectOption<
  string | ColumnFieldOption | DataBindingSourceInfo | StyleSheetTarget
>;

@Injectable({ providedIn: 'root' })
export class SelectKnobDataService {
  private readonly actions$ = inject(Actions);
  private readonly alertsApiService = inject(AlertsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly connectorTemplatesCollService = inject(ConnectorsTemplatesCollectionService);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly fontsService = inject(FontsService);
  private readonly formFieldControls = inject(FormFieldControlsService);
  private readonly intelliSenseService = inject(IntelliSenseService);
  private readonly notificationService = inject(NotificationService);
  private readonly pagesApi = inject(PagesApiService);
  private readonly searchAndFiltersWrapperService = inject(SearchAndFiltersWrapperService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly timezonesApiService = inject(TimeZonesApiService);

  private readonly fontsCache$ = new TimedObservableValueCache(1000);

  getSelectOptions(
    projectId: string,
    elementId: CanvasElementId,
    io: PropertiesConfigInputOptionsSelect,
    model: unknown,
    propertiesEditorModelUpdatedAction: any,
  ): Observable<SelectKnobOption[]> | undefined {
    let options$: Observable<SelectKnobOption[]> | undefined;

    switch (io.dataSourceType) {
      case 'list':
      case 'image-list': {
        options$ = of(io.options);
        break;
      }
      case 'sort':
      case 'model': {
        options$ = this.handleModelOptions(io, propertiesEditorModelUpdatedAction, model);
        break;
      }
      case 'api': {
        const source = io.modelKey as PropertiesConfigInputOptionsSelectApiSelectors;
        switch (source) {
          case 'LIST_PAGES':
            if (!projectId) {
              return;
            }
            options$ = this.listPagesByType(projectId, 'Page');
            break;
          case 'LIST_PAGES_WITH_MAINDATASET':
            if (!projectId) {
              return;
            }
            options$ = this.listPagesWithMainDataset(projectId);
            break;
          case 'LIST_CONTROLS_FOR_FIELD':
            if (!io.modelData) {
              throw new Error('select-knob missing model data');
            }
            options$ = this.listControlsForField(elementId, model, io.modelData);
            break;
          case 'LIST_CONTROLS_FOR_FILTER':
            if (!io.modelData) {
              throw new Error('select-knob missing model data');
            }
            options$ = this.listControlsForFilter(elementId, model, io.modelData);
            break;
          case 'LIST_PAYMENT_CONNECTORS':
            options$ = this.listPaymentConnectors();
            break;
          case 'LIST_CONNECTION_SELECTOR_CONNECTORS':
            options$ = this.listConnectionSelectorsConnectors();
            break;
          case 'LIST_TIMEZONES':
            options$ = this.timezonesApiService.timeZones$.pipe(map((opt) => opt || []));
        }
        break;
      }
      case 'url': {
        if (!io?.asyncUrl) {
          throw new Error('data source missing asyncUrl');
        }
        options$ = from(this.fetchOptions(io.asyncUrl) || []) as Observable<SelectKnobOption[]>;
        break;
      }
      case 'dataBindings': {
        options$ = this.attachDataBindingsModel(io, elementId);
        break;
      }
      case 'schema': {
        options$ = this.handleSchemaOptions(io, elementId as string, model);
        break;
      }
      case 'dataSourceWithAlert': {
        options$ = this.getDataSourcesWithAlert();
        break;
      }
      case 'formActions': {
        options$ = this.getFormActions(elementId, model);
        break;
      }
      case 'fonts': {
        options$ = this.getFontsOptions();
        break;
      }
      case 'fontWeights': {
        options$ = this.getFontWeightOptions(io, model);
        break;
      }
      case 'styleSheets': {
        // Forced casting from string to CanvasElementType to avoid circular dependency with core module
        options$ = this.getStyleSheetsOptions(
          io.styleSheetsElementType as CanvasElementType | undefined,
        );
        break;
      }
      case 'usersColumns': {
        const dataType = io.modelData;
        options$ = this.schemaService.obtainUsersSchema().pipe(
          map(
            (users) =>
              users?.columns
                .filter(
                  (column) =>
                    column.id && this.schemaService.isColumnVisible(column.name, users.id),
                )
                .filter((column) => !dataType || column.dataType === dataType)
                .map((column) => ({
                  label: column.name,
                  value: column.name,
                })) ?? [],
          ),
        );
        break;
      }
      default:
        throw new Error('malformed data source');
    }

    return options$ || of([]);
  }

  setDataSourceWithAlert(
    elementId: string,
    dataSource: DataBindingSourceInfo,
    oldDataSource: Nullish<DataBindingSourceInfo>,
    query: LogicalQueryNode | null,
  ): void {
    this.store.dispatch(
      updateElementProps({
        elementId,
        path: 'data.?key:value',
        value: {
          key: 'value',
          dataSource,
          enabled: true,
        } as CanvasElementDataProperties,
      }),
    );

    // Refresh binding to update the schemas and cause side-effects (columns refresh/form regeneration/etc)
    this.store.dispatch(
      refreshBinding({
        elementId,
        bindingKey: 'value',
        isNewDataSource: oldDataSource?.id !== dataSource.id,
      }),
    );

    this.updateDataSourceFieldWithAlert(elementId, undefined, query);
  }

  updateDataSourceFieldWithAlert(
    elementId: string,
    newColumnId: string | undefined,
    query: LogicalQueryNode | null,
  ): void {
    type Params = {
      alertId: string | undefined;
      alertPath: string;
      condition: SingleColumnComparison | undefined;
    };
    query ??= getEmptyLogicalNode();

    const PARAMS: Partial<Record<CanvasElementType, (element: CanvasElementFrom<any>) => Params>> =
      {
        notificationButton: (notificationButton: CanvasElementFrom<'notificationButton'>) => {
          const colId =
            newColumnId !== undefined
              ? newColumnId
              : notificationButton.props.content.fields?.isUnread;
          const column = notificationButton.props.content.columns?.find(
            ({ id }) => id === colId,
          )?.name;
          const condition = column
            ? {
                names: [column],
                op: ComparisonOperator.Eq,
                value: true,
              }
            : undefined;
          const alertId = notificationButton.props.content.alert?.id;
          return { alertId, alertPath: 'content.alert', condition };
        },
      };
    this.store
      .select(getElementById(elementId))
      .pipe(first())
      .subscribe((element) => {
        const tableId = hasProps(element)
          ? (getValueByPath(element.props, 'data.?key:value.dataSource.id') as string | undefined)
          : undefined;
        if (!tableId || !element?.type) {
          return;
        }
        const params = PARAMS[element?.type]?.(element);
        if (!params) {
          return;
        }

        (params.alertId ? this.alertsApiService.getAlerts(tableId) : of([]))
          .pipe(
            map((alerts) => !!alerts.find(({ id }) => id === params.alertId)),
            switchMap((alertExists) =>
              iif(
                () => !alertExists,
                iif(
                  () => !!params.condition,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  this.alertsApiService.addAlert(tableId, params.condition!, elementId, query).pipe(
                    tap((response) => {
                      this.store.dispatch(
                        updateElementProps({
                          elementId,
                          path: params.alertPath,
                          value: { id: response.id, active: true },
                        }),
                      );
                    }),
                  ),
                  of(undefined), // don't add passive alert
                ),
                iif(
                  () => !!params.condition,
                  this.alertsApiService.updateAlert(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    params.alertId!,
                    tableId,
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    params.condition!,
                    elementId,
                    query,
                  ),
                  of(undefined), // don't update passive alert
                ).pipe(
                  tap(() => {
                    this.store.dispatch(
                      updateElementProps({
                        elementId,
                        path: `${params.alertPath}.active`,
                        value: !!params.condition,
                      }),
                    );
                  }),
                ),
              ),
            ),
          )
          .subscribe({
            error: (error) => {
              this.notificationService.error('Failed to set up Alert');
              console.error('Failed to set up Alert', error);
            },
          });
      });
  }

  //#region model

  private handleModelOptions(
    io: PropertiesConfigInputOptionsSelect,
    propertiesEditorModelUpdatedAction: any,
    model?: unknown,
  ): Observable<SelectKnobOption[]> {
    const key = io.modelKey;

    return this.actions$.pipe(
      ofType(propertiesEditorModelUpdatedAction),
      filter(({ event }) => event.path === key),
      map(({ event }) => event.value),
      startWith(getValueByPath(model, io?.modelKey)),
      map((value) => this.mapModelOptions(io, value)),
      filter(Boolean),
    );
  }

  private mapModelOptions(
    io: PropertiesConfigInputOptionsSelect,
    modelValue?: unknown,
  ): { label: any; value: any }[] | undefined {
    if (!io) {
      return;
    }
    const rawValues = [...((modelValue as unknown[]) || [])];
    const { labelField, valueField } = io;
    const options =
      rawValues.length && typeof rawValues[0] === 'string'
        ? rawValues.map((item: any) => ({ label: item, value: item }))
        : rawValues.map((item: any) => ({
            // If Column ID or Name is to be selected, make sure the latest known name is presented to the user, even if the item contains an outdated name
            label:
              valueField === COLUMN_ID_OR_NAME
                ? getColumnNameLatestOrUnavailable(this.schemaService.getColumnNameLatest(item))
                : item[labelField],
            value: getModelField(item, valueField),
            dataType: item['dataType'],
          }));

    return options;
  }

  //#endregion

  //#region api

  private listPagesByType(projectId: string, pageType: PageType): Observable<SelectKnobOption[]> {
    return this.pagesApi.getPages(projectId).pipe(
      map((pages) => {
        const matchingPages = pages.filter((page) => page.pageType === pageType);
        const options = matchingPages.map((item: any) => ({
          label: item['name'],
          value: item['id'],
        }));

        return options;
      }),
    );
  }

  private listPagesWithMainDataset(projectId: string): Observable<SelectKnobOption[]> {
    return this.pagesApi.getPages(projectId).pipe(
      map((pages) => {
        const matchingPages = pages.filter((page) => page.mainDatasetId);
        const options = matchingPages.map((item: any) => ({
          label: item['name'],
          value: item['id'],
          mainDatasetId: item['mainDatasetId'],
        }));

        return options;
      }),
    );
  }

  private listConnectionSelectorsConnectors(): Observable<SelectKnobOption[]> {
    const WHITELISTED_CONNECTORS: Set<ConnectorTarget | undefined> = new Set([
      'ConnectorSelector',
      'GoogleCalendar',
      'GoogleMail',
      'Shopify',
    ]);
    return combineLatest({
      connectors: this.connectorsCollService.entities$,
      templates: this.connectorTemplatesCollService.entities$,
    }).pipe(
      map(({ connectors, templates }) => {
        const configuredTargets = new Set<ConnectorTarget | undefined>();

        const connectorOptions = (connectors || [])
          .filter(
            (connector) =>
              !!connector.id &&
              connector.target === 'ConnectorSelector' &&
              (connector.connectorType === 'DataSource' || connector.connectorType === 'Api'),
          )
          .map((connector) => {
            configuredTargets.add(connector.target);
            configuredTargets.add(connector?.configuration?.target);
            return {
              label: `${connector.name}`,
              value: connector.id || '',
            };
          });
        const templateOptions = (templates || [])
          .filter(
            (template) =>
              WHITELISTED_CONNECTORS.has(template.target) &&
              template.target !== 'ConnectorSelector' &&
              !configuredTargets.has(template.target) &&
              !!template.id &&
              (template.connectorType === 'DataSource' || template.connectorType === 'Api'),
          )
          .map((template) => {
            return {
              label: template.target,
              value: template.id || '',
            };
          });

        const options = sortByKey(connectorOptions.concat(templateOptions), 'label');
        return options;
      }),
    );
  }

  private listPaymentConnectors(): Observable<SelectKnobOption[]> {
    const OPTIONS_PAYMENT_PROVIDER: SelectOption<string>[] = [
      { value: 'Foxy', label: 'Foxy' },
      { value: 'NMI', label: 'NMI' },
      { value: 'Square', label: 'Square' },
      { value: 'Stripe', label: 'Stripe' },
    ];
    return this.connectorsCollService.entities$.pipe(
      map((allConnectors) => {
        const options = allConnectors
          .filter(
            ({ target }) =>
              target === 'Foxy' || target === 'NMI' || target === 'Square' || target === 'Stripe',
          )
          .map((connector) => {
            return {
              label: `${connector.target}: ${connector.name}`,
              value: connector.id || '',
            };
          });
        return options.length ? options : OPTIONS_PAYMENT_PROVIDER;
      }),
    );
  }

  private listControlsForField(
    currentElementId: string,
    props: any,
    modelFieldPath: string,
  ): Observable<SelectKnobOption[]> | undefined {
    const modelItem = getValueByPath(props, modelFieldPath) as FormListModelField;
    const modelField = this.schemaService.getColumnName(modelItem.fieldId ?? modelItem.field) ?? '';
    return this.actions$.pipe(
      ofType(updateElementProps),
      filter(
        ({ elementId, path }) =>
          elementId === currentElementId &&
          (path === 'content.model' || path.startsWith('content.model.')),
      ),
      startWith({}), // emit initial values
      map(() => this.formFieldControls.getControlOptionsByField(props)[modelField]),
    );
  }

  private listControlsForFilter(
    currentElementId: string,
    props: any,
    filterFieldPath: string,
  ): Observable<SelectKnobOption[]> | undefined {
    const modelItem = getValueByPath<PropertiesConfigInputListFiltersModel>(props, filterFieldPath);
    if (!modelItem?.filterField) {
      return of([]);
    }
    const column$ = this.getColumnsForDataSource(
      { modelKey: 'value', dataBindingsMode: 'first-dataset' },
      props.data,
    ).pipe(map((columns) => findColumn(columns, modelItem.filterField)));

    return column$.pipe(
      switchMap((column) => {
        const filterElementType = this.searchAndFiltersWrapperService.getFilterControlType(
          currentElementId,
          modelItem.id,
        )?.type;

        if (!filterElementType) {
          return [];
        }

        return this.searchAndFiltersWrapperService.getControlTypesForField(
          column,
          filterElementType,
        );
      }),
    );
  }

  //#endregion

  //#region dataBindings

  private attachDataBindingsModel(
    io: PropertiesConfigInputOptionsSelect,
    elementId: CanvasElementId,
  ): Observable<SelectKnobOption[]> | undefined {
    if (!io.modelKey) {
      return;
    }

    return this.actions$.pipe(
      ofType(updateBinding),
      filter((action) => action.elementId === elementId),
      startWith({ elementId }), // emit initial values
      concatMap(() =>
        this.store.select(getElementById(elementId)).pipe(first(), filter(hasDataProps)),
      ),
      switchMap((element) => this.getColumnsForDataSource(io, element.props.data)),
      map((columns) => this.mapOptionsFromColumns(columns)),
    );
  }

  // todo [ls] attach to schema; extend its behavior to handle not only WFs
  private getColumnsForDataSource(
    io: { modelKey?: string; dataBindingsMode?: DataBindingsMode },
    dataConfig: CanvasElementDataProperties[] | undefined,
  ): Observable<DatasetColumn[] | undefined> {
    const columns = this.getBindingDataByModelKey(io, dataConfig)?.dataSource?.columns;

    switch (io.dataBindingsMode) {
      case 'normal':
        return of(columns);
      case 'first-dataset': {
        const data = this.getBindingDataByModelKey(io, dataConfig);
        if (
          data?.dataSource?.sourceType === 'context' ||
          data?.dataSource?.sourceType === 'data-filter'
        ) {
          // Find first dataset in the workflow (dataset in first GET node)
          return this.store.select(runtimeSelectors.selectWorkflowById(data.dataSource.id)).pipe(
            map((workflow) => workflow && findFirstNodeInWorkflowOfType(workflow, isGetNode)),
            switchMap((getNode) =>
              getNode?.datasetId
                ? // If dataset present -> use columns from the dataset
                  this.schemaService
                    .obtainSchema(getNode?.datasetId)
                    .pipe(map((schema) => schema?.columns))
                : // If dataset missing -> use default
                  of(columns),
            ),
          );
        } else {
          // If data source is not workflow -> use default
          return of(columns);
        }
      }
      default:
        throw new Error(
          io.dataBindingsMode
            ? 'dataBindingsMode not provided'
            : `Unexpected dataBindingsMode "${io.dataBindingsMode}"`,
        );
    }
  }

  private getBindingDataByModelKey(
    io: { modelKey?: string },
    bindings?: CanvasElementDataProperties[],
  ): CanvasElementDataProperties | undefined {
    if (!io?.modelKey) {
      return undefined;
    }

    return bindings?.find((binding) => binding.key === io?.modelKey);
  }

  //#endregion

  private handleSchemaOptions(
    io: PropertiesConfigInputOptionsSelect,
    elementId: CanvasElementId,
    model?: unknown,
  ): Observable<SelectKnobOption[]> | undefined {
    const ds = getValueByPath(model, io.modelKey) as DataBindingSourceInfo;
    if (!ds) {
      return;
    }

    let schema$: Observable<BlazeSchema | undefined> | undefined = undefined;
    if (ds.sourceType === 'context') {
      // Update the schema whenever the bindings get updated for this element
      // Or workflow schemas get updated
      const refreshTrigger$ = merge(
        this.actions$.pipe(
          ofType(updateBinding),
          filter((action) => action.elementId === elementId),
        ),
        this.store.select(runtimeSelectors.selectWorkflowsSchemas),
      );

      if (!io.dataBindingsMode || io.dataBindingsMode === 'normal') {
        schema$ = refreshTrigger$.pipe(
          withLatestFrom(this.store.select(getElementEntities)),
          concatMap(([, elements]) => {
            const element = elements[elementId] as CanvasElement;
            const ds = getValueByPath(model, io.modelKey) as DataBindingSourceInfo;

            return element ? this.schemaService.obtainSchema<WorkflowSchema>(ds.id, true) : EMPTY;
          }),
        );
      } else if (io.dataBindingsMode === 'first-dataset') {
        schema$ = refreshTrigger$.pipe(
          switchMap(() => {
            const ds = getValueByPath(model, io.modelKey) as DataBindingSourceInfo;
            return this.store
              .select(runtimeSelectors.selectWorkflowById(ds.id))
              .pipe(map((workflow) => [ds.id, workflow] as const));
          }),
          map(([workflowId, workflow]) => {
            if (!workflow) {
              throw new Error(`Workflow ${workflowId} not found`);
            }
            const { filterColumnsType, nodePredicate } =
              io as unknown as PropertiesConfigInputOptionsSelectDataValue;

            const schema = this.intelliSenseService.getSchemaForWorkflowAtStep(
              workflow,
              nodePredicate,
            );
            const columnTypesFilter = filterColumnsType;
            if (!columnTypesFilter?.length || !schema) {
              return schema;
            }

            return produce(schema, (draft) => {
              draft.columns = draft.columns.filter(
                (column: DatasetColumn) =>
                  (column.columnType && columnTypesFilter.includes(column.columnType)) ||
                  column.isComputed,
              );
            });
          }),
        );
      }
    } else if (ds.sourceType === 'dataset') {
      schema$ = this.schemaService.obtainSchema(ds.id);
    }

    return schema$?.pipe(map((schema) => this.mapOptionsFromColumns(schema?.columns)));
  }

  private getDataSourcesWithAlert(): Observable<SelectKnobOption[]> | undefined {
    return of(
      [...this.schemaService.getSchemas().values()]
        .filter((schema) => hasBlazeTableFlag(schema))
        .map((schema) => ({
          label: schema.name,
          value: {
            sourceType: 'dataset' as const,
            id: schema.id,
            columns: schema.columns,
            displayColumn: 'displayColumn' in schema ? schema.displayColumn : '',
          },
        })),
    );
  }

  private getFormActions(
    elementId: CanvasElementId,
    model?: unknown,
  ): Observable<SelectKnobOption[]> | undefined {
    return this.store.select(getElementById(elementId)).pipe(
      map((element) => {
        if (!element) {
          return [];
        }
        if (!isElementType(element, 'formContainer')) {
          throw new Error('Form container expected');
        }

        const formModel = model as FormContainerPropsModel;
        const selectedAction: FormActionType | undefined = formModel?.content.action;
        const options: SelectOption<FormActionType>[] = [];

        // Show pregenerated 'prompt' option if selected
        if (selectedAction === 'prompt') {
          options.push({ value: 'prompt', label: 'Prompt' });
        }

        // Add row is always available
        options.push({ value: 'insert', label: 'Add Row' });

        // Update row is always available
        options.push({ value: 'update', label: 'Update Row' });

        options.push({ value: 'custom', label: 'Custom' });
        return options;
      }),
    );
  }

  private getFontsOptions(): Observable<any> {
    return this.fontsCache$.getValue('fonts', () => this.fontsService.getFontsOptions());
  }

  private getFontWeightOptions(
    io: PropertiesConfigInputOptionsSelect,
    model?: unknown,
  ): Observable<SelectOption<FontWeightOption>[]> {
    const relatedFontFamily = getValueByPath(model, io.modelKey) as string;
    return this.fontsService.getFontWeightOptions(relatedFontFamily);
  }

  private getStyleSheetsOptions(
    elementType?: CanvasElementType,
  ): Observable<SelectKnobOption[]> | undefined {
    const sheets$ = elementType
      ? this.elementStylesService.observeStyleByElementType(elementType)
      : this.elementStylesService.sheets$;

    return sheets$.pipe(
      map((sheets) =>
        sheets.map((sheet) => {
          const value: StyleSheetTarget = {};
          if (fromStyles.isStyleGlobal(sheet)) {
            value.id = sheet.id;
          } else {
            value.name = sheet.name;
          }

          return {
            label: sheet.name,
            value,
          };
        }),
      ),
    );
  }

  //#region utils

  private fetchOptions(url: string): Promise<SelectOption[] | any[]> | undefined {
    return fetch(url)
      .then((response) => response.json())
      .catch((err) => {
        console.error('External API call error ', err);
        this.notificationService.error(`External API call error: ${err}`);
      });
  }

  private mapOptionsFromColumns(columns?: DatasetColumn[]): SelectKnobOption[] {
    const options = !columns
      ? []
      : columns
          .filter((column) => !column.isInternal)
          .map(
            (column): SchemaColumnSelectOption => ({
              label: column.name,
              value: getModelField(column, COLUMN_ID_OR_NAME),
              dataType: column.dataType,
              columnType: column.columnType,
            }),
          );

    return options;
  }

  //#endregion
}
